import { computed, hasInjectionContext, inject, isRef, type ComputedRef } from 'vue'

import type { ReportColumn } from '../models/column.types'

import { useWorkspace } from '@/features/account'
import { i18n } from '@/shared/i18n'
import { cur, useFormat, type Formatter, type RoundMode } from '@/shared/lib/format'

type CurrencyOverride = string | { code?: string; value?: string } | null

export interface FormatCellOptions {
  round?: RoundMode
}

export interface UseReportCellFormatter {
  formatCell: (
    value: unknown,
    column: ReportColumn | null | undefined,
    returnStr?: boolean,
    options?: FormatCellOptions,
  ) => string
  currencySuffix: ComputedRef<string>
}

const CACHE_LIMIT = 8000

function numeric(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string' || value.trim() === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/** Fields whose numeric-looking values are identifiers, not quantities (see tableRowIdentity). */
function isIdField(field: string): boolean {
  return (
    field === 'id' ||
    field === '_id' ||
    field === 'uuid' ||
    field.endsWith('_id') ||
    field.endsWith('Id') ||
    field.endsWith('ID')
  )
}

/**
 * Conservative numeric coercion for the untyped default branch (ORP-19313). Accepts a finite
 * `number`, or a string that is a *clean* decimal — optional leading `-`, no leading zeros beyond
 * `0`/`0.x`, no scientific notation, hex, thousands separators, or trailing text. Rejects
 * id-looking strings such as `"007"`, plus `"123abc"`, `""`, `"1e309"`. Unlike the lenient
 * `numeric()` used by the flagged branches, this never coerces `"007"` to `7`.
 */
function plainNumeric(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/u.test(trimmed)) return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

function overrideCode(value: CurrencyOverride): string | undefined {
  if (typeof value === 'string') return value || undefined
  return value?.code || value?.value || undefined
}

function readOverride(value: unknown): CurrencyOverride {
  const resolved = isRef(value) ? value.value : value
  if (typeof resolved === 'string' || resolved === null) return resolved
  if (typeof resolved === 'object' && resolved !== null) return resolved as CurrencyOverride
  return null
}

function cacheSet(cache: Map<string, string>, key: string, value: string): string {
  cache.set(key, value)
  if (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next().value
    if (oldest !== undefined) cache.delete(oldest)
  }
  return value
}

function roundOpt(round: RoundMode | undefined): { round: RoundMode } | Record<string, never> {
  // v3 `formatFixed` truncated the displayed value (`formatWithoutRounding`) instead of rounding.
  // Keep that as the cell-level default while still allowing the few explicit callers to opt in
  // to normal Intl rounding.
  return { round: round ?? 'trunc' }
}

function formatCurrencyValue(
  value: number | null,
  fmt: Formatter,
  currencySuffix: string,
  returnStr: boolean,
  round: RoundMode | undefined,
): string {
  const formatted = fmt.formatNumber(value, { heavyDefault: true, ...roundOpt(round) })
  // v3 deliberately appended the active workspace/account-center glyph after the formatted
  // number. `Intl` currency formatting cannot be used here: besides changing that established
  // cell contract, its natural currency precision bypasses the >=1000 heavy-number rule.
  return returnStr && formatted !== '-' ? `${formatted}${currencySuffix}` : formatted
}

function renderValue(
  value: unknown,
  column: ReportColumn,
  fmt: Formatter,
  currencySuffix: string,
  returnStr: boolean,
  serialized: string,
  round: RoundMode | undefined,
): string {
  const number = numeric(value)
  if (column.functionType === 'formatSecondsAsDuration') return fmt.formatDuration(number)
  if (column.percentage) return fmt.formatPercent(number, { scale: 'ratio', ...roundOpt(round) })
  if (column.currency) return formatCurrencyValue(number, fmt, currencySuffix, returnStr, round)
  if (column.date_col) return fmt.formatDate(value as string | number | Date)
  if (Array.isArray(value)) return fmt.formatList(value.map(String))

  switch (column.sub_type) {
    case 'percentage':
      return fmt.formatPercent(number, { scale: 'ratio', ...roundOpt(round) })
    case 'currency':
      return formatCurrencyValue(number, fmt, currencySuffix, returnStr, round)
    case 'numeric':
    case 'meta':
    case 'decimal':
      return fmt.formatNumber(number, { heavyDefault: true, ...roundOpt(round) })
    case 'date':
      return fmt.formatDate(value as string | number | Date)
    case 'datetime':
      return fmt.formatDateTime(value as string | number | Date)
    case 'array':
      return fmt.formatList([String(value)])
    default: {
      if (column.numeric)
        return fmt.formatNumber(number, { heavyDefault: true, ...roundOpt(round) })
      if (column.sub_type === undefined && !isIdField(column.field)) {
        const plain = plainNumeric(value)
        if (plain !== null)
          return fmt.formatNumber(plain, { heavyDefault: true, ...roundOpt(round) })
      }
      return typeof value === 'object' ? serialized : String(value)
    }
  }
}

/** Module 08's sole column-aware display formatter (ledger A7, contract 08 §9). */
export function useReportCellFormatter(): UseReportCellFormatter {
  const workspace = useWorkspace()
  const fmt = useFormat()
  const injected = hasInjectionContext()
    ? inject<unknown>('accountCenterSelectedCurrency', null)
    : null
  const currencyCode = computed(
    () => overrideCode(readOverride(injected)) ?? workspace.currency.value,
  )
  const currencySuffix = computed(() => cur.resolveCurrencySymbol(currencyCode.value, fmt.locale))
  const cache = new Map<string, string>()

  function workspaceName(value: unknown): string {
    const match = workspace.workspaces.value.find((item) => String(item.id) === String(value))
    return match?.label || '-'
  }

  function formatCell(
    value: unknown,
    column: ReportColumn | null | undefined,
    returnStr = true,
    options?: FormatCellOptions,
  ): string {
    if (!column) return '-'
    if (value == null || value === '') return '-'

    if (column.sub_type === 'wsName') return workspaceName(value)
    if (column.field.startsWith('is_branded')) {
      return i18n.global.t(
        value === true || value === 'true' ? 'reports.cell.branded' : 'reports.cell.nonBranded',
      )
    }

    let cacheValue: string
    try {
      cacheValue = JSON.stringify(value) ?? String(value)
    } catch {
      cacheValue = String(value)
    }
    const round = options?.round
    const key = [
      fmt.locale,
      currencyCode.value,
      column.field,
      column.sub_type,
      column.functionType,
      returnStr ? 1 : 0,
      round ?? '',
      cacheValue,
    ].join('|')
    const cached = cache.get(key)
    if (cached !== undefined) return cached

    const rendered = renderValue(
      value,
      column,
      fmt,
      currencySuffix.value,
      returnStr,
      cacheValue,
      round,
    )
    return cacheSet(cache, key, rendered)
  }

  return { formatCell, currencySuffix }
}
